import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { sendAnalyticsEvents } from '../../analytics/common'
import { EVENTS } from '../../analytics/events'
import { setAmplitudeUserProperties } from '../../analytics/amplitude'
import { setLandingType } from '../plans/plansSlice'
import {
  osName,
  osVersion,
  isDesktop,
  isIOS,
  isAndroid,
} from 'react-device-detect'
import { selectEventsData, setAnalyticsData, setEventDataList } from '../events/eventsSlice'
import { useLandingType, queryParser } from 'onboarding-initialization';
import landingTypesList from '../../helper/landingTypes';
import { selectUserUuid } from '../signup/signupSlice'
import MainHeadline from '../../UI/MainHeadline'
import MainDescription from '../../UI/MainDescription'
import UIButton from '../../UI/UIButton'
import useImagesSrc from '../../helper/useImagesSrc'
import { usePreloadImages } from '../../helper/usePreloadImages'
import { useAnalyticsData } from '../checkout/helper'
import Q1 from '../questions/Q1'

const StyledWelcomeImage = styled.div`
  background-image: ${({ imgPath }) => imgPath && `url('${imgPath}')`};
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  display: flex;
  max-width: 375px;
  max-height: 345px;
  min-height: 150px;
  height: 100%;
  width: 100%;
  margin-top: 24px;

  @media screen and (max-width: 450px) {
    width: 100vw;
    margin-top: 0;
  }

  @media screen and (max-height: 600px) {
    margin-top: 5px;
  }
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;

  ${({ $isIntro }) =>
    $isIntro &&
    css`
      justify-content: flex-start;
      margin-top: 20px;
    `}


  @media screen and (max-height: 600px) {
    max-height: 450px;
  }

  ${({ withDisclaimer }) => withDisclaimer && `
    @media screen and (max-width: 450px) {
      padding-bottom: 175px;
    }
  `}
`;

const Welcome = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [eventParams, setEventParams] = useState(null);
  const eventsData = useSelector(selectEventsData);
  const userUuid = useSelector(selectUserUuid);
  const analyticsParams = useAnalyticsData();
  const { imagesSources } = useImagesSrc();
  const {
    location: { pathname },
  } = props;
  const landingParam = pathname.replace(/\//g, '');
  const { landingType, paywallType } = useLandingType(
    landingParam,
    landingTypesList,
  );
  const isIntroOffer = landingParam === 'intro_offer';

  usePreloadImages(imagesSources.about);

  useEffect(() => {
    if (eventParams || !paywallType) return;

    const {
      history: {
        location: { search },
      },
    } = props;

    const { fbclid, ttclid, gclid, ...utms } = queryParser(search);
    const {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    } = utms;
    const facebookUtm = fbclid ? 'facebook' : null;
    const appDomain = `${window.location.origin}${landingParam.length ? '/' + landingParam : ''}`;
    const utmSource = utms['utm_source'] || facebookUtm || 'organic';
    utms['utm_source'] = utmSource;

    dispatch(setLandingType(paywallType));

    const localEventsData = {
      landing_type: `quiz_${landingType}`,
      app_domain: appDomain,
      app_name: 'Familo',
      landingType,
      appDomain,
      appName: 'Familo',
      fbclid,
      ttclid,
      gclid,
      utm: utms,
      utm_source: utmSource,
      os: `${osVersion} ${osName}`,
    };

    const userProperties = {
      AppDomain: appDomain,
      OSDevice: `${isDesktop ? 'desktop' : isIOS ? 'iOS' : isAndroid ? 'Android' : osName}`,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    };

    dispatch(setEventDataList(localEventsData));
    setAmplitudeUserProperties(userProperties);
    setEventParams(localEventsData);
  }, [props, dispatch, eventParams, userUuid, landingParam, landingType, paywallType]);

  useEffect(() => {
    if (!eventParams) return;
    sendAnalyticsEvents(EVENTS.sessionStart, eventParams)
    sendAnalyticsEvents(EVENTS.onboardingStarted, eventParams)
  }, [eventParams]);

  useEffect(() => {
    dispatch(setAnalyticsData(analyticsParams));
  }, [analyticsParams, dispatch]);

  const onContinueClick = () => {
    sendAnalyticsEvents(EVENTS.onboardingStartedSubmitted, eventsData);
    props.history.push('/about-us')
  };

  const withDisclaimer = paywallType === 'up_v2';

  if (isIntroOffer) {
    return (
      <MainWrapper $isIntro={isIntroOffer}>
        <Q1 history={props.history}/>
      </MainWrapper>
    );
  }

  return (
    <MainWrapper withDisclaimer={withDisclaimer}>
      <StyledWelcomeImage imgPath={'../assets/screens/welcome.png'} />
      <MainHeadline>{t('welcome')}</MainHeadline>
      <MainDescription>{t('know_were_your_family_is')}</MainDescription>
      <UIButton
        onClick={onContinueClick}
        customText={t('lets_go')}
        bottomPosition="16px"
        customBeforeBg="none"
        withHint={withDisclaimer}
      />
    </MainWrapper>
	);
};

export default Welcome
